import { defineStore } from 'pinia'
import type { LoginResponse, LoginType, PasswordRule } from '@/types/login-types.ts'
import { axiosClient, storeToken } from '@/utils/axios-client.ts'

export const PASSWORD_RULES: PasswordRule[] = [
  {
    label: 'Je wachtwoord moet minstens 8 tekens lang zijn.',
    regexp: /.{8}/,
  },
  {
    label: 'Je wachtwoord mag niet langer dan 512 tekens lang zijn.',
    regexp: /^.{0,512}$/,
  },
  {
    label: 'Je wachtwoord moet minstens 1 cijfer bevatten.',
    regexp: /\d/,
  },
  {
    label: 'Je wachtwoord moet minstens 1 hoofdletter (A-Z) bevatten.',
    regexp: /[A-Z]/,
  },
  {
    label: 'Je wachtwoord moet minstens 1 kleine letter (a-z) bevatten.',
    regexp: /[a-z]/,
  },
  {
    label: 'Je wachtwoord moet minstens 1 speciaal teken bevatten. (#?!@$%^&*-)',
    regexp: /[#?!@$%^&*-]/,
  },
]

export const useLoginStore = defineStore('login', () => {
  /**
   * Validates a password against a set of predefined rules and returns the rules that the password fails to satisfy.
   *
   * @param {string} password - The password to be validated.
   * @return {PasswordRule[]} An array of rule objects that the password fails to satisfy.
   */
  function validatePassword(password: string): PasswordRule[] {
    return PASSWORD_RULES.filter(rule => !rule.regexp.test(password))
  }

  async function verifyMfa(token: string, code: string, type: LoginType) {
    const response = await axiosClient.request<string>({
      url: '/auth/api/login/verify-mfa',
      method: 'POST',
      data: {
        mfaToken: token,
        code: code,
        authenticationType: type,
      },
    })

    storeToken(response.data)
    return response.data
  }

  async function login(username: string, password: string): Promise<LoginResponse> {
    const response = await axiosClient.request<LoginResponse>({
      url: '/auth/api/login',
      method: 'POST',
      data: { username, password },
    })

    if (response.data.authenticationType === 'NONE') {
      storeToken(response.data.token)
    }
    return response.data
  }

  async function forgotPassword(email: string) {
    await axiosClient.request({
      url: '/auth/api/reset-password',
      method: 'POST',
      data: { username: email },
    })
  }

  async function processSsoLogin() {
    const legacyResponse = await axiosClient.request<string>({
      url: import.meta.env['VITE_LEGACY_API'] + '/auth/api/ssoToken',
      method: 'GET',
      withCredentials: true,
    })

    const response = await axiosClient.request<string>({
      url: '/auth/idf',
      method: 'POST',
      data: {
        token: legacyResponse.data,
      },
    })

    storeToken(response.data)
  }

  async function changePassword(token: string, password: string, passwordRepeat: string) {
    await axiosClient.request({
      url: '/auth/api/reset-password/confirm',
      method: 'POST',
      data: { token: token, password: password, 'password-repeating': passwordRepeat },
    })
  }

  return {
    login,
    forgotPassword,
    processSsoLogin,
    changePassword,
    validatePassword,
    verifyMfa,
  }
})
